// Twin of zl-api `packages/features/src/SpeedyFlow/hooks/usePopupChannel.ts`. Change both together.

import { useCallback, useEffect, useRef } from 'preact/hooks';

interface UsePopupChannelOptions {
  /**
   * The `window.open` name. Named rather than blank so a second open reuses the same tab instead of
   * stacking them — which is also why an invocation must retire the one before it.
   */
  target: string;
  /**
   * The message types this channel answers to. Anything else posted to the page is ignored and
   * leaves the channel running: a page has one `message` listener per open popup, and other things
   * on the origin talk over it.
   */
  messages: readonly string[];
  /** One of `messages` arrived. The channel has already torn this invocation down. */
  onMessage: (type: string) => void;
  /** The tab closed without saying anything. Torn down already, as above. */
  onClosed: () => void;
}

/** How often a manually closed tab is noticed. */
const CLOSED_POLL_MS = 500;

/**
 * The popup half of a flow that must not tear down the page behind it: open a named tab, listen for
 * what it reports, and notice if the visitor just closes it.
 *
 * Both users of this — Stripe checkout and social login — had their own copy of the same lifecycle,
 * and each copy had leaked in its own way. What is delicate is not any one step but the teardown,
 * so it lives here once:
 *
 * - **Every invocation owns its own cleanup**, closed over rather than reached through the ref.
 *   Reaching through the ref meant that opening a second popup orphaned the first: its interval
 *   could no longer clear itself, so once the newer cleanup had nulled the ref it went on firing
 *   forever.
 * - **A cleanup only stands down as the current one if it still is**, so retiring an old invocation
 *   cannot disarm the new one that replaced it.
 * - **Tearing down means the watcher and the listener**, not a flag. Flagging alone left a 500 ms
 *   interval alive for the life of the page, still dispatching outcomes into a flow that had been
 *   dismissed.
 *
 * The channel tears itself down *before* handing an outcome on, so a consumer can never be told
 * twice about the same tab.
 */
export const usePopupChannel = ({ target, messages, onMessage, onClosed }: UsePopupChannelOptions) => {
  const popupRef = useRef<Window | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * The latest options, so `open` never has to be rebuilt and a caller passing inline callbacks or
   * a literal `messages` array cannot silently re-arm the channel mid-flow.
   */
  const optionsRef = useRef({ messages, onMessage, onClosed });
  optionsRef.current = { messages, onMessage, onClosed };

  /** Stop watching whichever invocation is currently running. Safe to call repeatedly. */
  const stop = useCallback(() => {
    cleanupRef.current?.();
  }, []);

  useEffect(() => stop, [stop]);

  /**
   * Claim a tab now, while the click that triggered this is still being handled.
   *
   * Browsers generally only allow `window.open` during a user gesture. Anything that has to make an
   * async round trip before it knows the URL — creating a Stripe checkout session, say — would
   * otherwise open its tab too late to count as solicited, silently demoting the whole flow to the
   * same-tab fallback. Reserving a blank tab up front and loading the URL into it later keeps the
   * new tab the normal path.
   *
   * Note this is not observable under Playwright, which is more permissive than a real browser about
   * popups, so a passing e2e run does not prove real users are unaffected.
   */
  const reserve = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      return;
    }
    popupRef.current = window.open('', target);
  }, [target]);

  /** Close a tab reserved for something that never happened, so no blank tab is left behind. */
  const release = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  /**
   * Open `url` in the tab and start watching it. `false` means the browser blocked the popup, which
   * is the caller's cue to fall back to a same-tab redirect carrying its state.
   */
  const open = useCallback(
    (url: string) => {
      // Whatever the last call left running is finished with, and its tab is the one about to be
      // reused.
      cleanupRef.current?.();

      // Prefer the tab reserved during the click; fall back to opening one now.
      const reserved = popupRef.current && !popupRef.current.closed ? popupRef.current : null;
      if (reserved) {
        reserved.location.href = url;
      }
      const popup = reserved ?? window.open(url, target);
      popupRef.current = popup;

      if (!popup) {
        return false;
      }

      let cleanup = () => {};

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        const type = event.data?.type;
        if (typeof type !== 'string' || !optionsRef.current.messages.includes(type)) {
          return;
        }
        cleanup();
        optionsRef.current.onMessage(type);
      };

      const closedWatcher = setInterval(() => {
        if (!popup.closed) {
          return;
        }
        cleanup();
        optionsRef.current.onClosed();
      }, CLOSED_POLL_MS);

      cleanup = () => {
        clearInterval(closedWatcher);
        window.removeEventListener('message', handleMessage);
        // Only stand down as the current cleanup if this invocation still is it.
        if (cleanupRef.current === cleanup) {
          cleanupRef.current = null;
        }
      };
      cleanupRef.current = cleanup;

      window.addEventListener('message', handleMessage);
      return true;
    },
    [target],
  );

  return { open, reserve, release, stop };
};

export default usePopupChannel;
